import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabase';
import { Webhook, RefreshCw, AlertCircle, CheckCircle, Clock, XCircle, Eye, ExternalLink, Filter, CheckSquare } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

interface PlugAndPayWebhook {
  id: string;
  order_id: string;
  email: string;
  firstname: string | null;
  lastname: string | null;
  company: string | null;
  quantity: number;
  total_eur: number;
  product_name: string;
  credits_amount: number;
  matched_user_id: string | null;
  status: string;
  processed_at: string | null;
  processed_by: string | null;
  error_message: string | null;
  webhook_data: any;
  created_at: string;
}

export function AdminWebhooks() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState<PlugAndPayWebhook[]>([]);
  const [selectedWebhook, setSelectedWebhook] = useState<PlugAndPayWebhook | null>(null);
  const [showPayload, setShowPayload] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  const webhookUrl = `https://aszkqjnlwicwymhubcda.supabase.co/functions/v1/plugandpay-webhook`;

  const loadWebhooks = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('plugandpay_webhooks')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setWebhooks(data || []);
    } catch (error) {
      console.error('Error loading webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebhooks();
  }, [statusFilter]);

  const handleResolveWebhook = async () => {
    if (!selectedWebhook) return;

    setProcessing(true);
    try {
      // Get current user's admin ID
      const { data: { user: authUser } } = await supabase.auth.getUser();

      const { data: adminUser } = await supabase
        .from('admin_users')
        .select('id')
        .eq('auth_user_id', authUser?.id)
        .maybeSingle();

      // Update webhook status to resolved
      const { error: webhookError } = await supabase
        .from('plugandpay_webhooks')
        .update({
          status: 'resolved',
          processed_at: new Date().toISOString(),
          processed_by: adminUser?.id || null,
        })
        .eq('id', selectedWebhook.id);

      if (webhookError) throw webhookError;

      alert('Webhook succesvol gemarkeerd als afgehandeld!');
      setShowResolveModal(false);
      loadWebhooks();
    } catch (error) {
      console.error('Error resolving webhook:', error);
      alert('Fout bij het afhandelen van webhook');
    } finally {
      setProcessing(false);
    }
  };

  const getSummary = () => {
    return {
      total: webhooks.length,
      pending: webhooks.filter(w => w.status === 'pending').length,
      ambiguous: webhooks.filter(w => w.status === 'ambiguous').length,
      error: webhooks.filter(w => w.status === 'error').length,
      processed: webhooks.filter(w => w.status === 'processed').length,
      resolved: webhooks.filter(w => w.status === 'resolved').length,
    };
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: any }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      processed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      resolved: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckSquare },
      error: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      ambiguous: { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertCircle },
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const summary = getSummary();

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Webhook className="w-6 h-6" />
                Plug & Pay Webhooks
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Betaling logs en verwerking status
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  {statusFilter === 'all' ? 'Alle' : statusFilter}
                </button>
                {showFilterDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="py-1">
                      {['all', 'pending', 'ambiguous', 'error', 'resolved', 'processed'].map((status) => (
                        <button
                          key={status}
                          onClick={() => {
                            setStatusFilter(status);
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            statusFilter === status ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {status === 'all' ? 'Alle statussen' : status}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={loadWebhooks}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Ververs
              </button>
            </div>
          </div>

          <div className="grid grid-cols-6 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Totaal</div>
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
            </div>
            <div className="bg-white border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{summary.pending}</div>
            </div>
            <div className="bg-white border border-orange-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Ambiguous</div>
              <div className="text-2xl font-bold text-orange-600">{summary.ambiguous}</div>
            </div>
            <div className="bg-white border border-red-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Error</div>
              <div className="text-2xl font-bold text-red-600">{summary.error}</div>
            </div>
            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Resolved</div>
              <div className="text-2xl font-bold text-blue-600">{summary.resolved}</div>
            </div>
            <div className="bg-white border border-green-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Processed</div>
              <div className="text-2xl font-bold text-green-600">{summary.processed}</div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">Webhook URL (voor Zapier):</h3>
            <code className="block bg-white px-3 py-2 rounded border border-blue-200 text-sm font-mono text-blue-800 break-all">
              {webhookUrl}
            </code>
            <p className="text-xs text-blue-700 mt-2">
              Configureer deze URL in je Zapier webhook action om betalingen automatisch te verwerken.
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {webhooks.length === 0 ? (
            <div className="text-center py-12">
              <Webhook className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Geen webhooks ontvangen</p>
              <p className="text-sm text-gray-500 mt-1">
                Betalingen via Zapier verschijnen hier automatisch
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Ontvangen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Order ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Klant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Bedrag</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Credits</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {webhooks.map((webhook) => (
                    <tr key={webhook.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(webhook.created_at).toLocaleString('nl-NL')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                          {webhook.order_id}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div>
                          <div className="font-medium text-gray-900">
                            {webhook.firstname} {webhook.lastname}
                          </div>
                          <div className="text-gray-600 text-xs">{webhook.email}</div>
                          {webhook.company && (
                            <div className="text-gray-500 text-xs">{webhook.company}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {webhook.quantity}x {webhook.product_name}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        €{webhook.total_eur.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {webhook.credits_amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getStatusBadge(webhook.status)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedWebhook(webhook);
                              setShowPayload(true);
                            }}
                            className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                            title="Bekijk details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {(webhook.status === 'pending' || webhook.status === 'ambiguous' || webhook.status === 'error') && (
                            <button
                              onClick={() => {
                                setSelectedWebhook(webhook);
                                setShowResolveModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                              title="Markeer als afgehandeld"
                            >
                              <CheckSquare className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showPayload && selectedWebhook && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Webhook Details</h3>
                <button
                  onClick={() => setShowPayload(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                      <div className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded">
                        {selectedWebhook.order_id}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <div>{getStatusBadge(selectedWebhook.status)}</div>
                    </div>
                  </div>

                  {selectedWebhook.error_message && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-medium text-red-900 mb-1">Error</div>
                          <div className="text-sm text-red-800">{selectedWebhook.error_message}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Raw Payload</label>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                      {JSON.stringify(selectedWebhook.webhook_data, null, 2)}
                    </pre>
                  </div>

                  {selectedWebhook.matched_user_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Matched User</label>
                      <a
                        href={`/admin/users/${selectedWebhook.matched_user_id}`}
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View User Profile
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowPayload(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        )}

        {showResolveModal && selectedWebhook && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Webhook Afhandelen</h3>
                <button
                  onClick={() => setShowResolveModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Order ID:</span>
                      <span className="ml-2 font-mono font-medium">{selectedWebhook.order_id}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 font-medium">{selectedWebhook.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Klant:</span>
                      <span className="ml-2 font-medium">{selectedWebhook.firstname} {selectedWebhook.lastname}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Bedrijf:</span>
                      <span className="ml-2 font-medium">{selectedWebhook.company || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Bedrag:</span>
                      <span className="ml-2 font-medium">€{selectedWebhook.total_eur.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Credits:</span>
                      <span className="ml-2 font-medium text-blue-600">{selectedWebhook.credits_amount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {selectedWebhook.error_message && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="text-sm font-medium text-orange-900 mb-1">Reden:</div>
                    <div className="text-sm text-orange-800">{selectedWebhook.error_message}</div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium mb-2">Deze webhook wordt gemarkeerd als handmatig afgehandeld.</p>
                      <p>Credits worden <strong>NIET</strong> automatisch toegekend. Je moet zelf:</p>
                      <ul className="list-disc ml-5 mt-2 space-y-1">
                        <li>Credits handmatig toekennen aan de juiste gebruiker (via User Detail pagina)</li>
                        <li>Contact opnemen met de klant over het juiste e-mailadres</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowResolveModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={processing}
                >
                  Annuleren
                </button>
                <button
                  onClick={handleResolveWebhook}
                  disabled={processing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {processing && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {processing ? 'Bezig...' : 'Markeer als Afgehandeld'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
